import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, BellSimple, Spinner, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * FounderPilotPage — Stage 20B+ Wave F.
 *
 * Founder-only read-only pilot dashboard. Pulls one endpoint and renders
 * it as a calm 1-page summary. No charts, no vanity. Truth only.
 *
 * Path: /founder/pilot
 */

const API = process.env.REACT_APP_BACKEND_URL;
const PAPER = '#FBFAF6';
const NAVY = '#0E1C2E';
const NAVY_SOFT = 'rgba(14, 28, 46, 0.55)';
const TEAL = '#2FB3A0';
const HAIRLINE = 'rgba(14, 28, 46, 0.10)';

const Stat = ({ label, value, target, hint, testid }) => {
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  const onTarget = target != null && !Number.isNaN(numericValue) && numericValue >= target;
  return (
    <div
      className="rounded-2xl p-5"
      style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
      data-testid={testid}
    >
      <p
        className="font-sans text-[10.5px] uppercase font-medium mb-2"
        style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}
      >
        {label}
      </p>
      <div className="flex items-baseline gap-3">
        <p
          className="font-serif text-[34px] leading-none tracking-[-0.01em]"
          style={{ color: NAVY }}
        >
          {value ?? '–'}
        </p>
        {target != null && (
          <p
            className="font-sans text-[12px]"
            style={{ color: onTarget ? TEAL : NAVY_SOFT }}
            data-testid={`${testid}-target`}
          >
            / {target} {onTarget ? '✓' : ''}
          </p>
        )}
      </div>
      {hint && (
        <p className="font-sans text-[12px] mt-2" style={{ color: NAVY_SOFT }}>
          {hint}
        </p>
      )}
    </div>
  );
};

const FounderPilotPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reminding, setReminding] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/founder/pilot/summary`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/api/founder/pilot/users.csv`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kindra_pilot_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV export failed');
    }
  };

  const remindInactive = async () => {
    if (reminding) return;
    setReminding(true);
    try {
      const res = await axios.post(`${API}/api/founder/pilot/remind-inactive`, { days_inactive: 7 });
      toast.success(`Reminded ${res.data.reminded_count} · skipped ${res.data.skipped_count}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setReminding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: PAPER }}>
        <Spinner size={28} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-10" style={{ backgroundColor: PAPER }}>
        <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-700 text-[13px] flex items-center gap-2 mb-6">
          <ArrowLeft size={14} /> Back
        </button>
        <p className="font-serif text-[22px]" style={{ color: NAVY }}>Couldn't load the pilot summary.</p>
        <p className="font-mono text-[12px] text-slate-500 mt-2">{String(error)}</p>
      </div>
    );
  }

  const t = data.success_targets || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAPER }} data-testid="founder-pilot-page">
      <header
        className="w-full"
        style={{ backgroundColor: PAPER, borderBottom: `1px solid ${HAIRLINE}` }}
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-[#0E1C2E] text-[12.5px] flex items-center gap-2">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={remindInactive}
              disabled={reminding}
              className="font-sans text-[12.5px] px-4 py-2 rounded-full hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-50"
              style={{ color: NAVY, border: `1px solid ${HAIRLINE}` }}
              data-testid="pilot-remind-inactive-btn"
            >
              {reminding ? <Spinner size={14} className="animate-spin" /> : <BellSimple size={14} />}
              Remind inactive (7+ days)
            </button>
            <button
              onClick={downloadCsv}
              className="font-sans text-[12.5px] px-4 py-2 rounded-full hover:bg-white transition-colors flex items-center gap-2"
              style={{ color: NAVY, border: `1px solid ${HAIRLINE}` }}
              data-testid="pilot-export-csv-btn"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <p className="font-sans text-[10.5px] uppercase font-medium mb-3" style={{ color: TEAL, letterSpacing: '0.22em' }}>
          Pilot Mission Control
        </p>
        <h1 className="font-serif text-[36px] md:text-[44px] leading-[1.1] tracking-[-0.01em]" style={{ color: NAVY }}>
          Truth, not vanity.
        </h1>
        <p className="font-sans mt-4 text-[15px]" style={{ color: NAVY_SOFT }}>
          {data.window === 'last_7_days' ? 'Last 7 days · ' : ''}
          {data.now ? new Date(data.now).toLocaleString() : ''}
        </p>

        {/* Success targets — the founder's stated goals */}
        <section className="mt-12">
          <h2 className="font-sans text-[11px] uppercase font-medium mb-5" style={{ color: NAVY_SOFT, letterSpacing: '0.2em' }}>
            Success targets
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <Stat label="Invited"           value={t.invited?.actual}           target={t.invited?.target}           testid="stat-invited" />
            <Stat label="Activated"         value={t.activated?.actual}         target={t.activated?.target}         testid="stat-activated" hint="onboarding done + 1+ chat or upload" />
            <Stat label="Uploaded a report" value={t.uploads?.actual}           target={t.uploads?.target}           testid="stat-uploads" />
            <Stat label="Week-2 returns"    value={t.week_two_returns?.actual}  target={t.week_two_returns?.target}  testid="stat-week2" />
            <Stat label="Loved it"          value={t.loved?.actual}             target={t.loved?.target}             testid="stat-loved"  hint="thumbs-up or 3+ chats" />
            <Stat label="Paid intent"       value={t.paid_intent?.actual}       target={t.paid_intent?.target}       testid="stat-paid"   hint="checkout_started" />
          </div>
        </section>

        {/* This week */}
        <section className="mt-14">
          <h2 className="font-sans text-[11px] uppercase font-medium mb-5" style={{ color: NAVY_SOFT, letterSpacing: '0.2em' }}>
            This week
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Stat label="Active pilot users"   value={data.active_pilot_users}     testid="stat-active" />
            <Stat label="Uploads this week"    value={data.uploads_this_week}      testid="stat-uploads-week" />
            <Stat label="Chats this week"      value={data.chats_this_week}        testid="stat-chats-week" />
            <Stat label="Appointments added"   value={data.appointments_this_week} testid="stat-appts-week" />
          </div>
        </section>

        {/* Returns + signals */}
        <section className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }} data-testid="card-return-rate">
            <p className="font-sans text-[10.5px] uppercase font-medium" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>
              Week-2 return rate
            </p>
            <p className="font-serif text-[44px] leading-none tracking-[-0.01em] mt-3" style={{ color: NAVY }}>
              {data.week_two_return?.rate ?? 0}%
            </p>
            <p className="font-sans mt-3 text-[13.5px]" style={{ color: NAVY_SOFT }}>
              {data.week_two_return?.returned ?? 0} of {data.week_two_return?.eligible ?? 0} eligible users came back in the last 7 days.
            </p>
          </div>

          <div className="rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }} data-testid="card-drop-off">
            <p className="font-sans text-[10.5px] uppercase font-medium" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>
              Top drop-off step
            </p>
            {data.top_drop_off ? (
              <>
                <p className="font-serif text-[24px] leading-[1.25] tracking-[-0.005em] mt-3" style={{ color: NAVY }}>
                  {data.top_drop_off.from} → {data.top_drop_off.to}
                </p>
                <p className="font-sans mt-2 text-[14px]" style={{ color: NAVY_SOFT }}>
                  Losing <strong>{data.top_drop_off.drop_pct}%</strong> at this step.
                </p>
              </>
            ) : (
              <p className="font-sans mt-3 text-[14px]" style={{ color: NAVY_SOFT }}>No funnel data yet.</p>
            )}
          </div>
        </section>

        {/* Cohort signals */}
        <section className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <Stat label="Loved" value={data.loved_count} testid="stat-loved-cohort" hint="thumbs-up given OR 3+ chats" />
          <Stat label="Churned" value={data.churned_count} testid="stat-churned-cohort" hint="signed up >7d ago, no activity 7d" />
          <Stat label="Paid intent (checkout)" value={data.paid_intent_count} testid="stat-paid-intent-cohort" />
        </section>

        <p className="font-sans mt-16 text-[12px]" style={{ color: NAVY_SOFT }} data-testid="pilot-footer">
          Founder-only · Numbers are computed live from telemetry · No vanity charts, only truth.
        </p>
      </main>
    </div>
  );
};

export default FounderPilotPage;
